import React, { useEffect } from 'react';
import PhoneFormat from 'app/main/components/PhoneFormat';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import FuseChipSelect from '@fuse/core/FuseChipSelect';
import RolesSelect from 'app/main/components/RolesSelect';
import { companyDepartmentOptions } from 'app/constants/companyDepartments';
import { titleOptions } from 'app/constants/titles';
import { positionOptions } from 'app/constants/positions';
import { getPositions, selectPositionNames } from '../../store/positionsSlice';
import { getTitles, selectTitleNames } from '../../store/titlesSlice';
import {
  getCompanyDepartments,
  selectCompanyDepartmentNames
} from '../../store/companyDepartmentsSlice';
import UsDateField from 'app/main/components/UsDateField';
import ImportantDocuments from '../../components/ImportantDocuments';
import PreferredBusinessName from '../../components/PreferredBusinessName';
import usePermission from 'app/hooks/usePermission';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];
const stateOptions = US_STATES.map(state => ({ value: state, label: state }));

const useStyles = makeStyles(() => ({
  button: { width: 54, height: 54, borderRadius: 8, border: '1px solid', padding: 0 },
  addButton: { backgroundColor: '#fff', color: '#bdbdbd', borderColor: '#bdbdbd' },
  deleteButton: { backgroundColor: '#fff', color: '#e53935', borderColor: '#e53935' }
}));

const selectStyles = {
  control: base => ({ ...base, minHeight: 54, height: 54 }),
  valueContainer: base => ({ ...base, height: 54, paddingTop: 0, paddingBottom: 0 }),
  indicatorsContainer: base => ({ ...base, height: 54 }),
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

const selectDisplayProps = {
  isSearchable: false,
  valueContainerStyle: { padding: 0, maxHeight: 40 },
  placeholderStyle: { fontSize: '14px' },
  singleValueStyle: {
    width: '100%', boxSizing: 'border-box', fontSize: '14px', lineHeight: '19px', padding: '18.5px 14px'
  },
  optionStyle: { boxSizing: 'border-box', fontSize: '14px', lineHeight: '19px' }
};

function ProfessionalInfoTab({ form, setForm, handleChange }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isAdmin = usePermission('users.edit.all');
  const positions = useSelector(selectPositionNames);
  const titles = useSelector(selectTitleNames);
  const departments = useSelector(selectCompanyDepartmentNames);
  const stateLicenses = form.stateLicenses || [];

  // The three lists an administrator keeps under General Settings.
  useEffect(() => {
    dispatch(getPositions());
    dispatch(getTitles());
    dispatch(getCompanyDepartments());
  }, [dispatch]);

  const updateLicense = (index, field, value) => setForm({
    ...form,
    stateLicenses: stateLicenses.map((license, i) => i === index ? { ...license, [field]: value } : license)
  });
  const addLicense = () => setForm({ ...form, stateLicenses: [...stateLicenses, { state: '', number: '' }] });
  const removeLicense = index => setForm({ ...form, stateLicenses: stateLicenses.filter((_, i) => i !== index) });

  return (
    <div>
      <PreferredBusinessName form={form} handleChange={handleChange} />

      <UsDateField fullWidth className="mt-8 mb-16" id="startingDate" name="startingDate"
        label="Starting Date" value={form.startingDate || ''} onChange={handleChange} />

      <FuseChipSelect className="w-full my-16" id="companyDepartment" name="companyDepartment"
        value={form.companyDepartment ? { value: form.companyDepartment, label: form.companyDepartment } : null}
        options={companyDepartmentOptions(form.companyDepartment, departments)}
        onChange={selected => setForm({ ...form, companyDepartment: selected?.value || '' })}
        textFieldProps={{ label: 'Company Department', InputLabelProps: { shrink: true }, variant: 'outlined' }}
        styles={selectStyles} {...selectDisplayProps} />

      {/* Bound to `position`, the field an onboarding form fills in and the one the
          User schema actually calls position. The options are the list an
          administrator keeps under General Settings; `positionOptions` keeps whatever
          this profile already holds, whether a form collected it as free text or an
          administrator has since removed it. */}
      <FuseChipSelect className="w-full my-16" id="position" name="position"
        value={form.position ? { value: form.position, label: form.position } : null}
        options={positionOptions(form.position, positions)}
        onChange={selected => setForm({ ...form, position: selected?.value || '' })}
        textFieldProps={{ label: 'Position', InputLabelProps: { shrink: true }, variant: 'outlined' }}
        styles={selectStyles} {...selectDisplayProps} />

      {/* How senior this person is in that position, which is a different question
          from what the position is. */}
      <FuseChipSelect className="w-full my-16" id="title" name="title"
        value={form.title ? { value: form.title, label: form.title } : null}
        options={titleOptions(form.title, titles)}
        onChange={selected => setForm({ ...form, title: selected?.value || '' })}
        textFieldProps={{ label: 'Title', InputLabelProps: { shrink: true }, variant: 'outlined' }}
        styles={selectStyles} {...selectDisplayProps} />

      {/* The roles that decide what this account can do. It was labelled "Company
          Department" until that became a field of its own. */}
      {/* Shown so a person can see their own level, never editable here: the API
          strips `role` from a self-edit whatever this sends. */}
      <RolesSelect id="role" name="role" label="User Role" form={form} setForm={setForm}
        value={form.role} error={!form.role} isDisabled />

      <TextField fullWidth className="mt-8 mb-16" id="businessPhoneNumber" name="businessPhoneNumber"
        label="Business Phone Number" value={form.businessPhoneNumber || ''}
        variant="outlined" onChange={handleChange}
        InputProps={{ inputComponent: PhoneFormat }} />

      <TextField fullWidth type="email" className={`mt-8 mb-16 ${!isAdmin || form.active ? 'bg-gray-100' : ''}`}
        id="iloanEmail" name="iloanEmail" label="Company Email" value={form.iloanEmail || ''}
        variant="outlined" onChange={handleChange} InputProps={{ readOnly: !isAdmin || form.active }}
        disabled={!isAdmin || form.active} />

      <TextField fullWidth className="mt-8 mb-16" id="website" name="website" label="Website"
        placeholder="https://example.com" value={form.website || ''}
        variant="outlined" onChange={handleChange} />

      <Typography variant="h6" className="mt-24 mb-16">Licenses</Typography>

      <div className="flex items-center gap-12 mt-8 mb-16">
        <Typography className="font-600 whitespace-no-wrap">NMLS ID:</Typography>
        <TextField id="nmlsId" name="nmlsId" value={form.nmlsId || ''} variant="outlined"
          onChange={handleChange} style={{ width: 180 }} inputProps={{ 'aria-label': 'NMLS ID' }} />
      </div>
      {stateLicenses.map((license, index) => (
        <div key={license._id || index} className="flex items-start mb-16 gap-12 flex-wrap">
          <div style={{ flex: '1 1 0%', minWidth: 240 }}>
            <FuseChipSelect value={license.state ? { value: license.state, label: license.state } : null}
              options={stateOptions} onChange={selected => updateLicense(index, 'state', selected?.value || '')}
              textFieldProps={{ label: 'State', InputLabelProps: { shrink: true }, variant: 'outlined' }}
              menuPosition="fixed" menuPortalTarget={document.body} styles={selectStyles} {...selectDisplayProps} />
          </div>
          <div style={{ flex: '1 1 0%', minWidth: 240, display: 'flex', gap: 12 }}>
            <TextField fullWidth label="License Number" variant="outlined" value={license.number || ''}
              onChange={event => updateLicense(index, 'number', event.target.value)} />
            {index === stateLicenses.length - 1 && <IconButton className={clsx(classes.button, classes.addButton)} onClick={addLicense}><Icon>add</Icon></IconButton>}
            {stateLicenses.length > 1 && <IconButton className={clsx(classes.button, classes.deleteButton)} onClick={() => removeLicense(index)}><Icon>close</Icon></IconButton>}
          </div>
        </div>
      ))}

      <Box mt={4} mb={2}><Typography variant="h6">Tech Stack</Typography></Box>
      {[
        ['floifyApplicationLink', 'Floify Application Link', 'url'],
        ['floifyUsername', 'Floify Username', 'text'],
        ['encompassUsername', 'Encompass Username', 'text'],
        ['encompassId', 'Encompass ID', 'text'],
        ['surefireUsername', 'Surefire Username', 'text']
      ].map(([name, label, type]) => {
        const adminOnly = name === 'encompassId';
        return <TextField key={name} fullWidth type={type}
          className={`mt-8 mb-16 ${adminOnly && !isAdmin ? 'bg-gray-100' : ''}`}
          id={name} name={name} label={label} value={form[name] || ''} variant="outlined"
          onChange={handleChange} InputProps={{ readOnly: adminOnly && !isAdmin }}
          disabled={adminOnly && !isAdmin} />;
      })}

      <Box mt={4} mb={2}><Typography variant="h6">Important Documents</Typography></Box>
      <ImportantDocuments userId={form.id} form={form} setForm={setForm} classes={classes} />
    </div>
  );
}

export default ProfessionalInfoTab;
